import { AuthProvider } from 'react-admin';

import { config } from '../../config';
import { localStorageIdTokenKey, sessionStorageTokenKey, sessionStorageUserDataKey } from '../../constants';
import { httpClient } from '../../httpClient';
import { rolePermission } from '../../utils';

type AppleLoginResponse = {
  user: object;
  expiresAt: string;
};

const beginOAuthInNewTab = () => {
  return new Promise((resolve) => { 
    const signInWindow = window.open(`${config.API}/auth/apple`, '_blank', 'noopener,noreferrer');
    const storageListener = () => {
      try {
        const idToken = localStorage.getItem(localStorageIdTokenKey);
        if (idToken) {
          signInWindow?.close();
          window.removeEventListener('storage', storageListener);
          localStorage.removeItem(localStorageIdTokenKey);
          return resolve(idToken);
        }
      } catch (e) {
        window.removeEventListener('storage', storageListener);
      }
    }
    window.addEventListener('storage', storageListener);
  })
};

const login = async (): Promise<AppleLoginResponse> => {
  const idToken = await beginOAuthInNewTab();

  const response = await httpClient('/auth/apple/sign-in?origin=podiya-web-admin', {
    method: 'POST',
    body: JSON.stringify({ idToken })
  });

  if (response.status === 201) {
    const tokenHeader = response.headers.get('authorization') || response.headers.get('x-amzn-Remapped-authorization');
    const token = tokenHeader?.split(' ').pop();
    sessionStorage.setItem(sessionStorageTokenKey, token as string);

    const { user } = response.json;

    if (!rolePermission.auth(user.role)) {
      await logout();
      return Promise.reject('Forbidden');
    }
    
    return user;
  }

  return Promise.reject('Failed to sign in with Apple');
};

const logout = async () => {
  const token = sessionStorage.getItem(sessionStorageTokenKey);
  sessionStorage.removeItem(sessionStorageTokenKey);
  sessionStorage.removeItem(sessionStorageUserDataKey);

  if (!token) {
    return Promise.resolve();
  }

  const response = await httpClient('/auth/logout', { method: 'POST', token });

  if (response.status === 200) {
    return Promise.resolve();
  }

  return Promise.reject();
};

// TODO: define error type
const checkError = async (error: any) => {
  if (error.statusCode === 401 && error.error === 'Unauthorized') {
    await logout();
    return Promise.reject(error);
  }
};

const checkAuth = async () => {
  const token = sessionStorage.getItem(sessionStorageTokenKey);

  if (!token) {
    return Promise.reject();
  }

  const podiyaResponse = await httpClient('/users/me');

  if (
    podiyaResponse.status !== 200 ||
    !rolePermission.auth(podiyaResponse.json.role)
  ) {
    await logout();
    return Promise.reject('Forbidden');
  }

  sessionStorage.setItem(
    sessionStorageUserDataKey,
    JSON.stringify(podiyaResponse.json),
  );
};

const getIdentity = () => {
  // implemented in bridge, since it's common logic
  return Promise.reject('Not implemented.')
};

const getPermissions = () => {
  // implemented in bridge, since it's common logic
  return Promise.reject('Not implemented.')
};

const appleAuthProvider: AuthProvider = {
  login,
  logout,
  checkError,
  checkAuth,
  getPermissions,
  getIdentity,
};

export default appleAuthProvider;
